/**
 * File upload component for MentorWallet admin.
 * Uploads images to Supabase Storage and returns the public URL.
 */

import { useState, useRef } from 'react'
import { Upload, X, Loader2 } from 'lucide-react'
import { getSupabase } from '@/lib/supabase'

/**
 * Lo que el almacen acepta de verdad.
 *
 * El bucket `content-assets` limita tamano y tipo desde D-392. Aqui
 * se repiten los mismos numeros a proposito: sin esto, el navegador
 * sube seis megas para nada y lo que recibe el usuario es un mensaje
 * tecnico en ingles. Comprobarlo antes ahorra la espera y permite
 * decir lo que pasa en castellano.
 *
 * El `accept` del input no es una validacion: es una sugerencia del
 * selector de ficheros y se salta arrastrando cualquier cosa.
 */
const MAX_BYTES = 5 * 1024 * 1024
const TIPOS = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/avif']

interface MwFileUploadProps {
  courseSlug: string
  onUpload: (url: string) => void
  accept?: string
}

export function MwFileUpload({ courseSlug, onUpload, accept = 'image/*' }: MwFileUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFile = async (file: File) => {
    setError(null)

    if (!TIPOS.includes(file.type)) {
      setError('Solo se pueden subir imágenes: PNG, JPG, WEBP, GIF o AVIF.')
      return
    }
    if (file.size > MAX_BYTES) {
      const megas = (file.size / 1024 / 1024).toFixed(1)
      setError(`La imagen pesa ${megas} MB y el máximo son 5 MB. Redúcela antes de subirla.`)
      return
    }

    setUploading(true)

    try {
      const supabase = getSupabase()
      if (!supabase) throw new Error('Supabase no configurado')

      const ext = file.name.split('.').pop() ?? 'bin'
      const path = `${courseSlug}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`

      const { error: uploadErr } = await supabase.storage
        .from('content-assets')
        .upload(path, file, { contentType: file.type, upsert: false })

      if (uploadErr) throw new Error(uploadErr.message)

      const { data: urlData } = supabase.storage
        .from('content-assets')
        .getPublicUrl(path)

      const publicUrl = urlData.publicUrl
      setPreview(publicUrl)
      onUpload(publicUrl)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al subir')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0]
          if (file) handleFile(file)
        }}
      />

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="flex items-center gap-2 h-9 px-3 rounded-lg"
        style={{
          border: '1px dashed #D1D5DB',
          background: '#F9FAFB',
          fontSize: 12,
          color: '#6B7280',
          cursor: uploading ? 'wait' : 'pointer',
        }}
      >
        {uploading ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <Upload className="size-4" aria-hidden />
        )}
        {uploading ? 'Subiendo...' : 'Subir imagen'}
      </button>

      {error && (
        <div className="mt-2 flex items-center gap-1.5" style={{ fontSize: 11, color: '#DC2626' }}>
          <X className="size-3" aria-hidden />
          {error}
        </div>
      )}

      {preview && (
        <div className="mt-2 relative inline-block">
          <img
            src={preview}
            alt="Preview"
            className="rounded-lg"
            style={{ maxWidth: 200, maxHeight: 120, objectFit: 'cover' }}
          />
          <button
            type="button"
            onClick={() => { setPreview(null) }}
            className="absolute -top-1.5 -right-1.5 size-5 rounded-full flex items-center justify-center"
            style={{ background: '#DC2626', color: 'white', border: 'none', cursor: 'pointer' }}
          >
            <X className="size-3" aria-hidden />
          </button>
        </div>
      )}
    </div>
  )
}
